import React, { useRef, useEffect, useState, useCallback } from "react";
import type { TaktColorEntry } from "../../../../models";
import "./LegendContent.css";

interface Props {
  name: string;
  codes: Array<[string, string]>;
  colors: Record<string, TaktColorEntry>;
  height?: number;
  legendWidth?: number;
}

/** Legend content with scroll-aware bottom fade. */
const LegendContent: React.FC<Props> = React.memo(({ name, codes, colors, height, legendWidth }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);

  const checkFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4;
    const hasOverflow = el.scrollHeight > el.clientHeight + 2;
    setShowFade(hasOverflow && !atBottom);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkFade();
    el.addEventListener("scroll", checkFade);
    const ro = new ResizeObserver(checkFade);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkFade);
      ro.disconnect();
    };
  }, [checkFade]);

  return (
    <div className="takt-legend-scroll-wrapper" style={{ height: height || undefined, width: legendWidth || undefined }}>
      <div className="takt-legend-content" ref={scrollRef}>
        <div className="takt-legend-section-name">{name}</div>
        <div className="takt-legend-chips">
          {codes.map(([code, label]) => {
            const color = colors[code];
            return (
              <div key={code} className="takt-legend-chip" title={label}>
                <span
                  className="takt-legend-swatch"
                  style={{
                    backgroundColor: color?.fill || "#CCC",
                    color: color?.font || "#000",
                  }}
                >
                  {code}
                </span>
                <span className="takt-legend-chip-label">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className="takt-legend-fade"
        style={{ opacity: showFade ? 1 : 0 }}
      />
    </div>
  );
});

LegendContent.displayName = "LegendContent";

export default LegendContent;
